import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MoveReview } from '../../../games/analysis/review.ts';
import { applyMove, createInitial } from '../../../games/spec/interpreter.ts';
import type { BoardOf, SeatOf, State } from '../../../games/spec/interpreter.ts';
import type { RulesSpec } from '../../../games/spec/schema.ts';
import type { Seat } from '../../../shared/domain.ts';
import type { MatchReview } from '../../lib/api.ts';
import { Board } from '../match/Board.tsx';
import {
  announcement,
  assessment,
  columnName,
  evalShare,
  reasonSentences,
  tallySentence,
  verdictStyle,
} from './reviewText.ts';
import { useAnalysis } from './useAnalysis.ts';

export interface ReviewPageProps {
  readonly review: MatchReview;
  /** The seat the viewer played, when they played at all. Spectators pass null. */
  readonly yourSeat?: Seat | null;
  readonly onExit: () => void;
  /** What the exit button says, because the review is reached from two places. */
  readonly exitLabel?: string;
  readonly onRematch?: (() => void) | undefined;
  /** Engine thinking time per move. Lowered by tests; the default is measured. */
  readonly budgetMs?: number | undefined;
}

/** One position in the review: the board after a move, and where it landed. */
interface Ply {
  readonly param: number;
  readonly seat: number;
  readonly board: BoardOf<RulesSpec>;
  readonly cell: number;
  readonly winningLine: readonly number[] | null;
}

/**
 * Replay the log once, keeping every position it passed through.
 *
 * The interpreter is the only thing that knows where a piece lands, so the
 * screen asks it rather than counting discs itself. A log that fails to replay
 * stops here: showing a position the match never reached would be worse than
 * showing fewer moves.
 */
function buildPlies(rules: RulesSpec, firstSeat: number, params: readonly number[]): Ply[] {
  const plies: Ply[] = [];
  let state: State<RulesSpec> = createInitial(rules, firstSeat as SeatOf<RulesSpec>);

  for (const param of params) {
    if (state.status.kind !== 'in_play') break;
    const seat = state.toMove;
    const applied = applyMove(rules, state, seat, { param });
    if (!applied.ok) break;
    state = applied.state;
    plies.push({
      param,
      seat,
      board: state.board,
      cell: applied.primary,
      winningLine: state.status.kind === 'win' ? state.status.line : null,
    });
  }

  return plies;
}

/** How the match ended, in a sentence rather than a status code. */
function resultSentence(review: MatchReview, nameOf: (seat: number) => string): string {
  const winner = review.result === 'seat1_win' ? 1 : review.result === 'seat2_win' ? 2 : null;
  if (winner === null) return 'Drawn. The board filled up with nobody in front.';

  const name = nameOf(winner);
  switch (review.endReason) {
    case 'resign':
      return `${name} won by resignation.`;
    case 'abandon':
      return `${name} won: their opponent left.`;
    case 'timeout':
      return `${name} won on time.`;
    default:
      return `${name} won by completing a line.`;
  }
}

/**
 * Move-by-move review of a finished match.
 *
 * The board is the answer, not the prose. The engine's suggestion is drawn as a
 * ghost piece in the cell where it would have landed — the same device the
 * match screen uses to preview your own drop, so "what you should have played"
 * needs no legend and no arrow language. Everything to the right of the board
 * is secondary: a verdict for the move on screen, then the whole game as a list.
 */
export function ReviewPage({
  review,
  yourSeat = null,
  onExit,
  exitLabel = 'Back to lobby',
  onRematch,
  budgetMs,
}: ReviewPageProps) {
  const rules = review.spec.rules;
  const params = useMemo(
    () =>
      review.moves.filter((move) => move.param !== null).map((move) => move.param as number),
    [review.moves],
  );
  const plies = useMemo(
    () => buildPlies(rules, review.firstPlayerSeat, params),
    [rules, review.firstPlayerSeat, params],
  );

  const analysis = useAnalysis(
    plies.length === 0
      ? null
      : {
          rules,
          firstSeat: review.firstPlayerSeat,
          params: plies.map((ply) => ply.param),
          ...(budgetMs === undefined ? {} : { budgetMs }),
        },
  );

  // Open on the last move: the reason anyone opens a review is the move that
  // just lost them the game.
  const [index, setIndex] = useState(() => Math.max(0, plies.length - 1));
  useEffect(() => {
    setIndex(Math.max(0, plies.length - 1));
  }, [plies.length]);

  // The review replaces the match on screen without a page load, so focus has
  // to be moved deliberately — otherwise it is left on a button that no longer
  // exists, and the arrow keys below have nothing to listen to.
  useEffect(() => {
    sectionRef.current?.focus();
  }, []);

  const sectionRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Only pull focus into the list when the list already had it; moving the ply
  // with the Next button must not steal focus away from that button.
  const focusOption = useCallback((next: number) => {
    const list = listRef.current;
    if (list !== null && list.contains(document.activeElement)) {
      optionRefs.current[next]?.focus();
    }
  }, []);

  const go = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(plies.length - 1, next));
      setIndex(clamped);
      focusOption(clamped);
    },
    [plies.length, focusOption],
  );

  const nameOf = useCallback(
    (seat: number): string => review.seats[String(seat)]?.displayName ?? `Player ${String(seat)}`,
    [review.seats],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          go(index - 1);
          return;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          go(index + 1);
          return;
        case 'Home':
          event.preventDefault();
          go(0);
          return;
        case 'End':
          event.preventDefault();
          go(plies.length - 1);
          return;
        default:
      }
    },
    [go, index, plies.length],
  );

  const current = plies[index] ?? null;
  const entry: MoveReview | null = analysis.entries[index] ?? null;
  const seats = rules.seats;

  if (current === null) {
    return (
      <section className="review" aria-labelledby="review-heading">
        <h2 id="review-heading" className="review__heading">
          Game review
        </h2>
        <p className="review__empty">
          This match has no moves to review. {resultSentence(review, nameOf)}
        </p>
        <footer className="review__actions">
          <button type="button" className="button button--quiet" onClick={onExit}>
            {exitLabel}
          </button>
        </footer>
      </section>
    );
  }

  const suggestion =
    entry !== null && entry.bestParam !== entry.param
      ? {
          column: entry.bestParam,
          seat: entry.seat,
          label: `${nameOf(entry.seat)} should have played here`,
        }
      : null;

  const spoken = announcement(
    {
      index,
      total: plies.length,
      param: current.param,
      seat: current.seat,
      entry,
      analysing: analysis.status === 'running',
      analysedCount: analysis.entries.length,
    },
    nameOf,
    seats,
  );

  const share = entry === null ? 50 : evalShare(entry, seats[0] ?? 1);
  const reasons = entry === null ? [] : reasonSentences(entry, nameOf, seats);

  return (
    // The key handler sits on the section so the arrows work wherever focus is
    // inside the review. It is a shortcut layer over real buttons and a real
    // listbox, never the only way to move.
    <section
      className="review"
      aria-labelledby="review-heading"
      onKeyDown={onKeyDown}
      ref={sectionRef}
      tabIndex={-1}
    >
      <header className="review__head">
        <h2 id="review-heading" className="review__heading">
          Game review
        </h2>
        <p className="review__result">{resultSentence(review, nameOf)}</p>
        {analysis.status === 'ready' && analysis.tallies.length > 0 ? (
          <ul className="review__tallies">
            {analysis.tallies.map((tally) => (
              <li key={tally.seat} className={`review__tally review__tally--seat${tally.seat}`}>
                {tallySentence(tally, nameOf(tally.seat))}
              </li>
            ))}
          </ul>
        ) : null}
        {analysis.status === 'running' ? (
          <p className="review__progress">
            Analysing {analysis.entries.length} of {analysis.total}
            <span
              className="review__progressBar"
              aria-hidden="true"
              style={{
                '--progress': `${String(
                  analysis.total === 0
                    ? 0
                    : Math.round((analysis.entries.length / analysis.total) * 100),
                )}%`,
              } as React.CSSProperties}
            />
          </p>
        ) : null}
        {analysis.status === 'failed' ? (
          <p className="review__notice">
            The engine could not analyse this game, so there are no verdicts. The moves are all
            still here.
          </p>
        ) : null}
      </header>

      <div className="review__body">
        <div className="review__boardCol">
          <Board
            spec={review.spec}
            board={current.board}
            yourSeat={yourSeat}
            legalColumns={[]}
            pendingColumn={null}
            lastMoveIndex={current.cell}
            winningLine={current.winningLine}
            interactive={false}
            columnControls={false}
            annotation={suggestion}
            // With the column controls off nothing can call this: a finished
            // game has nowhere to drop a piece.
            onDrop={() => undefined}
          />

          <div className="review__nav">
            <button
              type="button"
              className="button button--quiet"
              onClick={() => go(0)}
              disabled={index === 0}
            >
              First
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => go(index - 1)}
              disabled={index === 0}
            >
              Previous
            </button>
            <p className="review__counter">
              Move {index + 1} of {plies.length}
            </p>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => go(index + 1)}
              disabled={index === plies.length - 1}
            >
              Next
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => go(plies.length - 1)}
              disabled={index === plies.length - 1}
            >
              Last
            </button>
          </div>

          <p className="review__hint">
            Left and right arrows step through the game. Home and End jump to the ends.
          </p>
        </div>

        <aside className="review__rail">
          <article className="review__verdict panel">
            <p className="review__played">
              <span
                className={`review__seatDot review__seatDot--seat${current.seat}`}
                aria-hidden="true"
              />
              {nameOf(current.seat)} played {columnName(current.param)}
            </p>

            {entry === null ? (
              <p className="review__pending">
                {analysis.status === 'failed' ? 'No verdict available.' : 'Analysing this move…'}
              </p>
            ) : (
              <>
                <p className={`verdict verdict--${entry.verdict}`}>
                  {/*
                    The tick is the non-colour channel: one short bar for a good
                    move, four tall heavy ones for a blunder. Count, height and
                    weight all carry the severity, so the chip still reads in
                    greyscale, in forced colours and through stream compression.
                  */}
                  <span className="verdict__tick" aria-hidden="true">
                    {Array.from(
                      { length: verdictStyle(entry.verdict).severity + 1 },
                      (_unused, bar) => (
                        <span key={bar} className="verdict__bar" />
                      ),
                    )}
                  </span>
                  {verdictStyle(entry.verdict).label}
                </p>

                {suggestion === null ? (
                  <p className="review__better">Nothing was better.</p>
                ) : (
                  <p className="review__better">
                    <span className="review__ghostKey" aria-hidden="true" />
                    {columnName(entry.bestParam)} was better: the outlined piece.
                  </p>
                )}

                {/*
                  The reason, which is the point of the whole screen: "column 4
                  was better" teaches somebody to copy an engine they will not
                  have next game, and "it blocked their line" teaches them to
                  see it. Absent when nothing could be proven — most opening
                  moves are simply reasonable, and filler here would train
                  people to ignore the one line that matters.
                */}
                {reasons.length > 0 ? (
                  <ul className="review__reasons">
                    {reasons.map((sentence) => (
                      <li key={sentence} className="review__reason">
                        {sentence}
                      </li>
                    ))}
                  </ul>
                ) : null}

                <p className="review__assessment">{assessment(entry, nameOf, seats)}</p>

                <div className="review__eval">
                  <span
                    className="review__evalBar"
                    aria-hidden="true"
                    style={{ '--share': `${String(share)}%` } as React.CSSProperties}
                  />
                </div>
              </>
            )}
          </article>

          <div
            className="review__moves"
            role="listbox"
            aria-label="Moves in this game"
            tabIndex={-1}
            ref={listRef}
          >
            {plies.map((ply, position) => {
              const record = analysis.entries[position] ?? null;
              const style = record === null ? null : verdictStyle(record.verdict);
              return (
                <div
                  key={position}
                  role="option"
                  aria-selected={position === index}
                  tabIndex={position === index ? 0 : -1}
                  ref={(element) => {
                    optionRefs.current[position] = element;
                  }}
                  className={`review__move${position === index ? ' review__move--current' : ''}`}
                  onClick={() => go(position)}
                >
                  <span className="review__moveNumber">{position + 1}</span>
                  <span
                    className={`review__seatDot review__seatDot--seat${ply.seat}`}
                    aria-hidden="true"
                  />
                  <span className="review__moveText">{columnName(ply.param)}</span>
                  {record !== null && style !== null ? (
                    <span className={`verdict verdict--${record.verdict} verdict--compact`}>
                      <span className="verdict__tick" aria-hidden="true">
                        {Array.from({ length: style.severity + 1 }, (_unused, bar) => (
                          <span key={bar} className="verdict__bar" />
                        ))}
                      </span>
                      {style.label}
                    </span>
                  ) : (
                    <span className="review__moveText review__moveText--muted">not yet</span>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      <footer className="review__actions">
        <button type="button" className="button button--quiet" onClick={onExit}>
          {exitLabel}
        </button>
        {onRematch ? (
          <button type="button" className="button button--primary" onClick={onRematch}>
            Play again
          </button>
        ) : null}
      </footer>

      {/*
        One live region for the whole screen. It carries the same sentence the
        board and the verdict card show, so a screen reader user navigating with
        the arrow keys hears the answer instead of a board redrawing silently.
      */}
      <p className="visually-hidden" aria-live="polite" data-testid="review-announcement">
        {spoken}
      </p>
    </section>
  );
}
